import React from 'react'
import { PaymentInterval, RecurlyPrice } from './Recurly'
import { UpgradeTideways6 } from './UpgradeTideways6'
import Money from '../helper/money'

interface Props {
    recurlyPricing: any
    upgrade: UpgradeTideways6
    newPlan: string
    requests: number
    teams: number
    coupon: string
    currency: string
    projectSizes: { [key: string]: number }
    interval: PaymentInterval
    showHeader: boolean
    currentPrice?: number
}
interface State {
    price: RecurlyPrice | null
}

export default class Summary extends React.Component<Props, State> {
    public static defaultProps = {
        showHeader: false,
    }
    constructor(props: Props) {
        super(props)
        this.state = {
            price: null,
        }
    }
    public componentDidMount() {
        this.updateRecurlyPricing()
    }

    public componentDidUpdate(prevProps) {
        if (prevProps.newPlan !== this.props.newPlan) {
            this.updateRecurlyPricing()
        } else if (prevProps.currency !== this.props.currency) {
            this.updateRecurlyPricing()
        } else if (prevProps.projectSizes !== this.props.projectSizes) {
            this.updateRecurlyPricing()
        } else if (prevProps.interval !== this.props.interval) {
            this.updateRecurlyPricing()
        } else if (prevProps.requests !== this.props.requests) {
            this.updateRecurlyPricing()
        } else if (prevProps.teams !== this.props.teams) {
            this.updateRecurlyPricing()
        }
    }

    public render() {
        if (this.state.price == null) {
            return null
        }

        const paymentInterval = this.props.interval === PaymentInterval.Monthly ? 'Monthly' : 'Yearly'
        const paymentPeriodName = this.props.interval === PaymentInterval.Monthly ? 'month' : 'year'

        const extraBoosters = this.calculateExtraBoosters()
        const planLabels = {}
        const teamBasedAccess = {}
        this.props.upgrade.availablePlans.forEach(planChoice => {
            planLabels[planChoice.name] = planChoice.label
            teamBasedAccess[planChoice.name] = planChoice.includesTeamBasedAccess
        })

        const teams = this.calculateTeamAddOns()
        const nf = new Intl.NumberFormat('en-US')

        return (
            <div>
                <div className="box">
                    {this.props.showHeader ? (
                        <div className="box-header">
                            <h3 className="box-title">Summary New Subscription Price</h3>
                        </div>
                    ) : null}
                    <div className="box-body box-body-larger">
                        <div className="row">
                            <div className="col-md-4">Plan</div>
                            <div className="col-md-3">{planLabels[this.props.newPlan]}</div>

                            <div className="col-md-3">
                                {this.state.price.now.plan}
                                {this.state.price.currency.symbol}/{paymentPeriodName}
                            </div>
                        </div>

                        {this.props.newPlan.includes('tideways8') ? null : (
                            <div className="row">
                                <div className="col-md-4">Increased Projects and Limits</div>
                                <div className="col-md-3">{extraBoosters} x</div>

                                <div className="col-md-3">
                                    {this.state.price.base.addons.apm_extra_boosters} {this.state.price.currency.symbol}/{paymentPeriodName}
                                </div>
                            </div>
                        )}

                        {this.props.requests > 0 ? (
                            <div className="row">
                                <div className="col-md-4">Monthly Requests</div>

                                <div className="col-md-3">{nf.format(this.props.requests)}</div>

                                <div className="col-md-3">
                                    {this.state.price.addons.requests} {this.state.price.currency.symbol}/{paymentPeriodName}
                                </div>
                            </div>
                        ) : null}

                        {teamBasedAccess[this.props.newPlan] ? (
                            <div className="row">
                                <div className="col-md-4">Additional Teams</div>

                                <div className="col-md-3">{teams} x</div>

                                <div className="col-md-3">
                                    {this.state.price.base.addons.apm_teams} {this.state.price.currency.symbol}/{paymentPeriodName}
                                </div>
                            </div>
                        ) : null}

                        {this.props.upgrade.coupon && (
                            <div className="row">
                                <div className="col-md-4">Discount with Coupon</div>

                                <div className="col-md-3">{this.props.upgrade.coupon}</div>

                                <div className="col-md-3">
                                    {this.state.price.now.discount} {this.state.price.currency.symbol} / {paymentPeriodName}
                                </div>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-4"></div>

                            <div className="col-md-7">
                                <hr />
                            </div>
                        </div>

                        {this.props.currentPrice !== undefined ? (
                            <div className="row">
                                <div className="col-md-4">Price until now:</div>
                                <div className="col-md-3"></div>
                                <div className="col-md-3">
                                    <Money value={this.props.currentPrice / 100} />
                                    {this.state.price.currency.symbol}
                                </div>
                                <div className="col-md-4"></div>
                                <div className="col-md-3"></div>
                                <div className="col-md-3">
                                    <Money
                                        value={(this.state.price.now.subtotal * 100 - this.props.currentPrice) / 100}
                                        displaySignAlways={true}
                                    />
                                    {this.state.price.currency.symbol}
                                </div>
                            </div>
                        ) : null}
                        <div className="row">
                            <div className="col-md-4">Total:</div>

                            <div className="col-md-3"></div>

                            <div className="col-md-3">
                                {this.state.price.now.subtotal} {this.state.price.currency.symbol} / {paymentPeriodName}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    private updateRecurlyPricing() {
        const plan = this.props.interval === PaymentInterval.Yearly ? this.props.newPlan + '_1y' : this.props.newPlan
        let pricing = this.props.recurlyPricing.plan(plan, { quantity: 1 })
        if (plan.includes('tideways8')) {
            pricing = pricing.addon('requests', { quantity: this.props.requests })
            pricing = pricing.addon('apm_teams', { quantity: this.calculateTeamAddOns() })
        } else {
            pricing = pricing.addon('apm_extra_boosters', { quantity: this.calculateExtraBoosters() })
            pricing = pricing.addon('apm_teams', { quantity: this.calculateTeamAddOns() })
        }

        if (this.props.coupon) {
            pricing = pricing.coupon(this.props.coupon)
        }

        pricing = pricing.currency(this.props.currency)

        pricing.done(price => {
            this.setState({ price })
        })
    }

    private calculateTeamAddOns() {
        const includedTeams = {
            tideways6_starter: 1,
            tideways6_basic: 1,
            tideways6_standard: 1,
            tideways6_pro: 5,
            tideways7_basic: 1,
            tideways7_standard: 1,
            tideways7_pro: 5,
            tideways8_team: 1,
            tideways8_business: 5,
            tideways8_enterprise: 5,
        }

        return Math.max(0, this.props.teams - (includedTeams[this.props.newPlan] || 1))
    }

    private calculateExtraBoosters() {
        let totalProjectSizes = 0
        this.props.upgrade.applications.map((application, id) => {
            totalProjectSizes += this.props.projectSizes[application.id]
        })
        return Math.max(0, totalProjectSizes - this.props.upgrade.includedProjectSize)
    }
}
